import type { ReactNode } from 'react';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuthState, useAuthActions } from './AuthContext';
import { useGlobalWebSocket } from './GlobalWebSocketState';
import {
  WalletActionsContext,
  WalletBalanceContext,
  type WalletActionsContextType,
  type WalletBalanceContextType,
} from './WalletContexts';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from '@/hooks/use-toast';
import logger from '@/lib/utils/logger';

function isUnknownRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

// Reward kinds with their own celebratory title in every locale; anything
// else falls back to the generic reward title so a future kind still gets
// a sensible toast.
const KNOWN_REWARD_KINDS: ReadonlySet<string> = new Set([
  'comeback_reward',
  'login_streak_reward',
  'first_session_message',
  'first_session_match',
  'first_session_second_character',
  'stage_promotion',
]);

interface WalletProviderProps {
  children: ReactNode;
}

export const WalletProvider: React.FC<WalletProviderProps> = ({ children }) => {
  const { t } = useTranslation('billing');
  const { user, isAuthenticated } = useAuthState();
  const { updateUserBalance } = useAuthActions();
  const queryClient = useQueryClient();
  const [balance, setBalance] = useState(user?.credits ?? 0);
  const [isLoading, setIsLoading] = useState(false);

  // Update balance when user changes
  useEffect(() => {
    if (user) {
      setBalance(user.credits);
    }
  }, [user]);

  // Stable refreshBalance callback - never changes reference
  const refreshBalance = useCallback(async () => {
    setIsLoading(true);
    try {
      // Trigger a refetch of user data
      await queryClient.invalidateQueries({ queryKey: ['current-user'] });
      await queryClient.invalidateQueries({ queryKey: ['balance'] });
      await queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    } finally {
      setIsLoading(false);
    }
  }, [queryClient]);

  // Subscribe to WebSocket for real-time balance updates
  const { subscribe } = useGlobalWebSocket();

  useEffect(() => {
    if (!isAuthenticated) return;

    const unsubscribe = subscribe((data) => {
      switch (data.type) {
        case 'balance_update': {
          if (!isFiniteNumber(data.new_balance)) {
            logger.error('Ignoring invalid balance_update WebSocket event', data);
            break;
          }

          const newBalance = data.new_balance;
          setBalance(newBalance);
          // Update user in auth context
          updateUserBalance(newBalance);
          // Also update query cache for consistency
          queryClient.setQueryData(['current-user'], (oldData: unknown): unknown => {
            if (isUnknownRecord(oldData)) {
              return {
                ...oldData,
                credits: newBalance,
              };
            }
            return oldData;
          });
          break;
        }

        case 'subscription_updated': {
          if (typeof data.plan_name !== 'string' || data.plan_name.trim() === '') {
            logger.error('Ignoring invalid subscription_updated WebSocket event', data);
            break;
          }

          // Trigger a full refresh of user data to get new subscription status
          void refreshBalance();
          toast({
            title: t('toasts.subscriptionUpdatedTitle'),
            description: t('toasts.subscriptionActivatedDescription', { plan: data.plan_name }),
          });
          break;
        }

        case 'transaction_completed': {
          const creditsAmount: unknown = data.credits_amount;
          if (!isFiniteNumber(data.new_balance)) {
            logger.error('Ignoring invalid transaction_completed WebSocket event', data);
            break;
          }
          if (typeof data.transaction_type !== 'string') {
            logger.error('Ignoring transaction_completed event with invalid transaction_type', data);
            break;
          }
          let purchaseCredits: number | undefined;
          if (data.transaction_type === 'purchase') {
            if (!isFiniteNumber(creditsAmount)) {
              logger.error('Ignoring purchase transaction_completed event with invalid credits_amount', data);
              break;
            }
            purchaseCredits = creditsAmount;
          }

          const newBalance = data.new_balance;
          setBalance(newBalance);
          // Update user in auth context
          updateUserBalance(newBalance);
          // Invalidate transaction queries
          void queryClient.invalidateQueries({ queryKey: ['transactions'] });

          // Show notification for purchases
          if (purchaseCredits !== undefined) {
            toast({
              title: t('toasts.coinsReceivedTitle'),
              description: t('toasts.coinsReceivedDescription', { credits: purchaseCredits }),
            });
          }
          break;
        }

        case 'insufficient_funds': {
          const requiredAmount: unknown = data.required_amount ?? data.required;
          const currentBalance: unknown = data.current_balance ?? data.current;
          if (!isFiniteNumber(requiredAmount) || !isFiniteNumber(currentBalance)) {
            logger.error('Ignoring invalid insufficient_funds WebSocket event', data);
            break;
          }
          const requiredCoins: number = requiredAmount;
          const currentCoins: number = currentBalance;

          toast({
            title: t('toasts.insufficientFundsTitle'),
            description: t('toasts.insufficientFundsDescription', {
              required: requiredCoins,
              current: currentCoins,
            }),
            variant: 'destructive',
          });
          break;
        }

        case 'reward_granted': {
          if (typeof data.reward_kind !== 'string') {
            logger.error('Ignoring reward_granted event with invalid reward_kind', data);
            break;
          }
          if (!isFiniteNumber(data.coins_earned)) {
            logger.error('Ignoring invalid reward_granted WebSocket event', data);
            break;
          }
          if (data.new_balance !== undefined && !isFiniteNumber(data.new_balance)) {
            logger.error('Ignoring reward_granted event with invalid new_balance', data);
            break;
          }

          // Escalating login-streak / comeback reward, first-session activation
          // rewards and stage-promotion celebrations all arrive as reward_granted.
          // balance_update also fires and updates the wallet; here we surface the
          // concrete coin amount with a kind-appropriate title (generic fallback
          // so any future reward_kind still gets a sensible toast).
          if (data.new_balance !== undefined) {
            setBalance(data.new_balance);
            updateUserBalance(data.new_balance);
          }
          const rewardTitle = KNOWN_REWARD_KINDS.has(data.reward_kind)
            ? t(`toasts.rewards.${data.reward_kind}`)
            : t('toasts.rewardFallbackTitle');
          toast({
            title: rewardTitle,
            description: t('toasts.rewardCoinsDescription', { coins: data.coins_earned }),
          });
          break;
        }
      }
    });

    return unsubscribe;
  }, [isAuthenticated, subscribe, updateUserBalance, queryClient, refreshBalance, t]);

  // Memoize balance state value
  const balanceValue = useMemo<WalletBalanceContextType>(
    () => ({
      balance,
      isLoading,
    }),
    [balance, isLoading]
  );

  // Memoize actions value - stable reference via useCallback
  const actionsValue = useMemo<WalletActionsContextType>(
    () => ({
      refreshBalance,
    }),
    [refreshBalance]
  );

  return (
    <WalletBalanceContext.Provider value={balanceValue}>
      <WalletActionsContext.Provider value={actionsValue}>{children}</WalletActionsContext.Provider>
    </WalletBalanceContext.Provider>
  );
};
